import {
  PennDotDistricts,
  AtGradeExpectedIntxTypes,
  UrbanSubIntxTypes,
  AtGradeIntxTypes,
  CrashSeverity,
  SafetyIntxType,
  AtGradeExpectedIntxTypesUrbanSub,
  AreaType,
} from "./SPFConstants";
import {
  RoundaboutSpfs,
  get3LegRoundaboutSPFs,
  get4LegRoundaboutSPFs,
  getExpectedCrashes,
  getFatalInjuryCrashRatio,
  getUrbanThreeLegSignalSpfs,
} from "./spfHelpers";
const CalibrationFactorforThreeLegMinor = {
  [PennDotDistricts.DISTRICT_1]: { total: 0.58, fatalInjury: 0.661 },
  [PennDotDistricts.DISTRICT_2]: { total: 0.434, fatalInjury: 0.442 },
  [PennDotDistricts.DISTRICT_3]: { total: 0.434, fatalInjury: 0.442 },
  [PennDotDistricts.DISTRICT_4]: { total: 0.731, fatalInjury: 1.0 },
  [PennDotDistricts.DISTRICT_5]: { total: 1.0, fatalInjury: 1.0 },
  [PennDotDistricts.DISTRICT_6]: { total: 1.0, fatalInjury: 1.0 },
  [PennDotDistricts.DISTRICT_8]: { total: 0.813, fatalInjury: 0.844 },
  [PennDotDistricts.DISTRICT_9]: { total: 0.727, fatalInjury: 0.844 },
  [PennDotDistricts.DISTRICT_10]: { total: 0.58, fatalInjury: 0.661 },
  [PennDotDistricts.DISTRICT_11]: { total: 0.58, fatalInjury: 0.661 },
  [PennDotDistricts.DISTRICT_12]: { total: 0.727, fatalInjury: 0.844 },
};

const getSPFsForIntxType = (intxType: SafetyIntxType) => {
  switch (intxType) {
    case SafetyIntxType.THREE_LEG_ALL_STOP:
      return {
        getTotalPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-10.16) *
          Math.pow(majorRoad.aadt, 0.618) *
          Math.pow(minorRoad.aadt, 0.534),

        getFatalInjuryPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-12.692) *
          Math.pow(majorRoad.aadt, 0.867) *
          Math.pow(minorRoad.aadt, 0.498),
      };
    case SafetyIntxType.THREE_LEG_MINOR_STOP:
      return {
        getTotalPredicted: ({
          majorRoad,
          minorRoad,
          pennDotDistrict,
        }: UrbanSubIntxTypes) =>
          Math.exp(-6.643) *
          Math.pow(majorRoad.aadt, 0.517) *
          Math.pow(minorRoad.aadt, 0.254) *
          Math.exp(-0.314 * Number(majorRoad.hasCrosswalk)) *
          Math.exp(0.158 * Number(majorRoad.speedLimit >= 40)) *
          CalibrationFactorforThreeLegMinor[pennDotDistrict].total,

        getFatalInjuryPredicted: ({
          majorRoad,
          minorRoad,
          pennDotDistrict,
        }: UrbanSubIntxTypes) =>
          Math.exp(-7.547) *
          Math.pow(majorRoad.aadt, 0.513) *
          Math.pow(minorRoad.aadt, 0.251) *
          Math.exp(0.218 * Number(majorRoad.speedLimit >= 40)) *
          CalibrationFactorforThreeLegMinor[pennDotDistrict].fatalInjury,
      };
    case SafetyIntxType.THREE_LEG_SIGNAL:
      return getUrbanThreeLegSignalSpfs();
    case SafetyIntxType.FOUR_LEG_MINOR_STOP:
      return {
        getTotalPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-6.594) *
          Math.pow(majorRoad.aadt, 0.286) *
          Math.pow(minorRoad.aadt, 0.643),

        getFatalInjuryPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-7.309) *
          Math.pow(majorRoad.aadt, 0.377) *
          Math.pow(minorRoad.aadt, 0.526),
      };
    case SafetyIntxType.FOUR_LEG_ALL_STOP:
      return {
        getTotalPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-11.032) * Math.pow(majorRoad.aadt + minorRoad.aadt, 1.233),

        getFatalInjuryPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-8.297) * Math.pow(majorRoad.aadt + minorRoad.aadt, 0.83),
      };
    case SafetyIntxType.FOUR_LEG_SIGNAL:
      return {
        getTotalPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-6.884) *
          Math.pow(majorRoad.aadt, 0.542) *
          Math.pow(minorRoad.aadt, 0.308),
        getFatalInjuryPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-9.127) *
          Math.pow(majorRoad.aadt, 0.684) *
          Math.pow(minorRoad.aadt, 0.333),
      };
    case SafetyIntxType.THREE_LEG_ROUNDABOUT:
      return get3LegRoundaboutSPFs(AreaType.URBAN);
    case SafetyIntxType.FOUR_LEG_ROUNDABOUT:
      return get4LegRoundaboutSPFs(AreaType.URBAN);
    default:
      throw new Error(`Could not find SPFs for intersection type: ${intxType}`);
  }
};

const getFatalInjuryPredictedInitial = (props: UrbanSubIntxTypes) => {
  return getSPFsForIntxType(props.intxType)?.getFatalInjuryPredicted(props);
};

const getPdoPredictedInitial = (props: UrbanSubIntxTypes) => {
  const { getPDOPredicted } = getSPFsForIntxType(
    props.intxType
  ) as RoundaboutSpfs;
  if (!getPDOPredicted) {
    throw new Error("Could not find getPDOPredicted function");
  }

  return getPDOPredicted(props);
};

export const getFatalInjuryPredicted = (props: UrbanSubIntxTypes): number => {
  if (
    props.intxType === SafetyIntxType.THREE_LEG_ROUNDABOUT ||
    props.intxType === SafetyIntxType.FOUR_LEG_ROUNDABOUT
  ) {
    const fiInitial = getFatalInjuryPredictedInitial(props);
    const totalPredicted = getTotalPredicted(props);
    const pdoInitial = getPdoPredictedInitial(props);

    return getFatalInjuryCrashRatio({
      fiInitial,
      pdoInitial,
      totalCrashes: totalPredicted,
    });
  }
  return getFatalInjuryPredictedInitial(props);
};

export const getTotalPredicted = (props: UrbanSubIntxTypes): number => {
  return getSPFsForIntxType(props.intxType)?.getTotalPredicted(props);
};

export const getTotalExpected = (props: AtGradeExpectedIntxTypes): number =>
  getExpectedCrashes({
    ...props,
    crashSeverity: CrashSeverity.TOTAL,
    getPredictedCrashes: getTotalPredicted,
  });

export const getFatalInjuryExpected = (
  props: AtGradeExpectedIntxTypesUrbanSub
): number => {
  if (
    props.intxType === SafetyIntxType.THREE_LEG_ROUNDABOUT ||
    props.intxType === SafetyIntxType.FOUR_LEG_ROUNDABOUT
  ) {
    const fiInitial = getFatalInjuryPredictedInitial(props);
    const totalExpected = getTotalExpected(props);
    const pdoInitial = getPdoPredictedInitial(props);

    return getFatalInjuryCrashRatio({
      fiInitial,
      pdoInitial,
      totalCrashes: totalExpected,
    });
  }
  return getExpectedCrashes({
    ...props,
    crashSeverity: CrashSeverity.FATAL_INJURY,
    getPredictedCrashes: getFatalInjuryPredicted,
  });
};
